import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { ArrowRight } from 'lucide-react';

const steps = [
  {
    num: '01',
    title: 'Book a Consultation',
    desc: 'Start with a 30–40 minute intake call with your coordinator. We learn about your loved one — their personality, their story, and what matters most to your family. No scripts, no pressure — just a warm conversation.',
  },
  {
    num: '02',
    title: 'Custom Interview Design',
    desc: 'Our team creates a personalized interview guide based on your family\'s unique story. We craft questions that draw out the moments, wisdom, and memories that matter most — so the recording session feels natural and meaningful.',
  },
  {
    num: '03',
    title: 'The In-Person Recording',
    desc: 'About 2 hours, paced entirely to your loved one\'s comfort. A guided conversation captured with professional video, audio, and photography. Our team handles all the equipment and direction — you just enjoy the moment.',
  },
  {
    num: '04',
    title: 'Professional Production',
    desc: 'Over approximately two weeks, we professionally edit the story film, produce the voice archive, write and print the life story book, curate the photo collection, and build the private digital heirloom page.',
  },
  {
    num: '05',
    title: 'Heirloom Delivery',
    desc: 'Your family receives a premium keepsake box containing the printed life story book, curated photo prints, a sealed envelope, a legacy letter, and a QR access card — all beautifully presented and ready to keep.',
  },
  {
    num: '06',
    title: 'The Guided Reveal',
    desc: 'Scan the access card with your phone. Their professionally edited story film begins to play. Their voice fills the room. The digital heirloom page holds everything — video, audio, book, photos, and life lessons — all in one private experience.',
  },
];

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-[#FCFBF9]">
      <SEO
        title="How It Works"
        path="/how-it-works"
        description="From a 30-minute intake call to receiving your heirloom box — learn the 6-step process behind a professionally guided legacy recording with Heirloom Stories Studio."
      />
      <Navbar />

      <section className="pt-32 sm:pt-40 pb-24 sm:pb-32">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-20">
            <p className="overline mb-6">The Process</p>
            <h1 data-testid="how-it-works-title" className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl font-medium text-[#1C1917] leading-tight mb-6">
              How It Works
            </h1>
            <p className="text-base sm:text-lg text-[#57534E] leading-relaxed max-w-xl mx-auto">
              A white-glove experience designed to feel effortless for your family. We handle everything — you just show up.
            </p>
          </div>

          <div className="space-y-20">
            {steps.map((step, i) => (
              <div
                key={i}
                data-testid={`step-${step.num}`}
                className="grid grid-cols-1 md:grid-cols-[80px_1fr] gap-6 md:gap-10 opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${i * 150}ms`, animationFillMode: 'forwards' }}
              >
                <span className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl font-light text-[#E7E5DF]">
                  {step.num}
                </span>
                <div className="border-t border-[#E7E5DF] pt-6">
                  <h2 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-medium text-[#1C1917] mb-4">
                    {step.title}
                  </h2>
                  <p className="text-base text-[#57534E] leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-20">
            <p className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl italic text-[#78716C] mb-10">
              No scripts. No awkwardness. No stress.
            </p>
            <Link
              to="/contact"
              data-testid="how-it-works-cta"
              className="inline-flex items-center justify-center bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] px-8 py-4 text-sm font-medium tracking-wide transition-all duration-300 gap-2"
            >
              Begin Your Story <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
